// src/modulos/Dashboard/dashboards/DiretoriaDashboard.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import useFirestore from '../../hooks/useFirestore';
import usePresence from '../../hooks/usePresence';
import styles from './Dashboard.module.css';

// React Icons
import {
    FiXCircle,
    FiTrendingUp,
    FiUsers,
    FiPackage,
    FiDollarSign,
    FiFileText,
    FiAlertCircle,
    FiCheckCircle,
    FiClock,
    FiBox,
    FiX,
    FiMapPin,
    FiTag,
    FiList,
    FiUser,
    FiInfo,
    FiCheck,
    FiEye,
    FiArrowRight,
    FiCalendar,
    FiActivity,
    FiAward,
    FiShoppingCart,
    FiTruck,
    FiSend,
    FiRefreshCw,
    FiChevronDown,
    FiChevronUp,
    FiEdit,
    FiTrash2,
    FiPlus,
    FiGitCommit,
    FiLogIn,
    FiLogOut
} from 'react-icons/fi';
import { FaChartLine, FaClipboardList } from 'react-icons/fa';

// ============================================
// TOAST COMPONENT
// ============================================
const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 5000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const getIcon = () => {
        if (type === 'success') return <FiCheck size={18} />;
        if (type === 'error') return <FiAlertCircle size={18} />;
        if (type === 'warning') return <FiAlertCircle size={18} />;
        return <FiInfo size={18} />;
    };

    return (
        <div className={`${styles.toast} ${styles[`toast${type.charAt(0).toUpperCase() + type.slice(1)}`]}`}>
            <span className={styles.toastIcon}>{getIcon()}</span>
            <span>{message}</span>
            <button onClick={onClose} className={styles.toastClose}>×</button>
        </div>
    );
};

// ============================================
// APPROVAL MODAL COMPONENT
// ============================================
const ApprovalModal = ({ order, type, onClose, onConfirm, t }) => {
    const [notes, setNotes] = useState('');
    const [isRejection, setIsRejection] = useState(false);
    const [loading, setLoading] = useState(false);

    if (!order) return null;

    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'R$ 0,00';
        return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    };

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm({
                orderId: order.id,
                orderCode: order.code,
                status: isRejection ? 'rejected' : 'approved',
                notes: notes,
                approvedBy: 'Diretoria',
                approvedAt: new Date().toISOString()
            });
            onClose();
        } catch (error) {
            // Error handled by parent
        } finally {
            setLoading(false);
        }
    };

    const isSalesOrder = type === 'sales';
    const orderTypeLabel = isSalesOrder ? 'Pedido de Venda' : 'Pedido de Compra';

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>
                        {isRejection ? (
                            <FiXCircle size={24} style={{ color: 'var(--danger-color)', marginRight: '10px' }} />
                        ) : (
                            <FiCheckCircle size={24} style={{ color: 'var(--success-color)', marginRight: '10px' }} />
                        )}
                        {isRejection ? 'Rejeitar' : 'Aprovar'} {orderTypeLabel}
                    </h3>
                    <button onClick={onClose} className={styles.modalCloseBtn}>
                        <FiX size={20} />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div style={{ marginBottom: '20px' }}>
                        <div className={styles.orderDetailsGrid}>
                            <div>
                                <label className={styles.orderDetailsLabel}>Código</label>
                                <div className={styles.orderDetailsValue}>{order.code}</div>
                            </div>
                            <div>
                                <label className={styles.orderDetailsLabel}>Data</label>
                                <div className={styles.orderDetailsValue}>{order.orderDate || new Date(order.createdAt?.toDate?.() || order.createdAt).toLocaleDateString('pt-BR')}</div>
                            </div>
                            <div>
                                <label className={styles.orderDetailsLabel}>{isSalesOrder ? 'Cliente' : 'Fornecedor'}</label>
                                <div className={styles.orderDetailsValue}>{isSalesOrder ? order.clientName : order.supplierName}</div>
                            </div>
                            <div>
                                <label className={styles.orderDetailsLabel}>Valor Total</label>
                                <div className={styles.orderDetailsValue} style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                    {formatCurrency(order.calculatedValues?.total || order.total || 0)}
                                </div>
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                                <label className={styles.orderDetailsLabel}>Itens</label>
                                <div style={{ maxHeight: '120px', overflow: 'auto', fontSize: '0.95rem' }}>
                                    {order.items?.map((item, idx) => (
                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border-color)' }}>
                                            <span>{item.name || item.productName || 'Item'}</span>
                                            <span>{item.quantity || 0} x {formatCurrency(item.unitPrice || 0)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className={styles.orderDetailsLabel} style={{ display: 'block', marginBottom: '6px' }}>
                            {isRejection ? 'Motivo da Rejeição' : 'Observações (opcional)'}
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className={styles.modalTextarea}
                            rows={3}
                            placeholder={isRejection ? 'Informe o motivo da rejeição...' : 'Adicione observações sobre a aprovação...'}
                        />
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        onClick={() => setIsRejection(!isRejection)}
                        className={styles.modalCloseFooterBtn}
                        disabled={loading}
                        style={{
                            backgroundColor: isRejection ? 'transparent' : 'var(--danger-light)',
                            color: isRejection ? 'var(--text-secondary)' : 'var(--danger-color)'
                        }}
                    >
                        {isRejection ? '← Voltar para Aprovar' : 'Rejeitar Pedido'}
                    </button>
                    <button
                        onClick={handleConfirm}
                        className={styles.modalActionBtn}
                        disabled={loading}
                        style={{
                            backgroundColor: isRejection ? 'var(--danger-color)' : 'var(--success-color)',
                        }}
                    >
                        {loading ? (
                            <FiRefreshCw size={18} className={styles.spinner} style={{ animation: 'spin 0.8s linear infinite' }} />
                        ) : (
                            isRejection ? <FiX size={18} /> : <FiCheck size={18} />
                        )}
                        {isRejection ? 'Confirmar Rejeição' : 'Aprovar Pedido'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ACTIVITY ITEM COMPONENT
// ============================================
const ActivityItem = ({ activity }) => {
    const getActivityIcon = (type) => {
        const iconMap = {
            'login': <FiLogIn size={16} />,
            'logout': <FiLogOut size={16} />,
            'order_created': <FiShoppingCart size={16} />,
            'order_approved': <FiCheckCircle size={16} />,
            'order_rejected': <FiXCircle size={16} />,
            'order_updated': <FiEdit size={16} />,
            'order_deleted': <FiTrash2 size={16} />,
            'product_created': <FiPackage size={16} />,
            'product_updated': <FiEdit size={16} />,
            'product_deleted': <FiTrash2 size={16} />,
            'project_created': <FiFileText size={16} />,
            'project_updated': <FiEdit size={16} />,
            'ticket_created': <FiAlertCircle size={16} />,
            'ticket_updated': <FiEdit size={16} />,
            'ticket_closed': <FiCheck size={16} />,
            'user_updated': <FiUser size={16} />,
            'report_generated': <FiActivity size={16} />,
            'export': <FiSend size={16} />,
        };
        return iconMap[type] || <FiGitCommit size={16} />;
    };

    const getActivityColor = (type) => {
        const colorMap = {
            'login': 'var(--success-color)',
            'logout': 'var(--text-muted)',
            'order_created': 'var(--primary-color)',
            'order_approved': 'var(--success-color)',
            'order_rejected': 'var(--danger-color)',
            'order_updated': 'var(--warning-color)',
            'order_deleted': 'var(--danger-color)',
            'product_created': 'var(--info-color)',
            'product_updated': 'var(--warning-color)',
            'product_deleted': 'var(--danger-color)',
            'project_created': 'var(--info-color)',
            'project_updated': 'var(--warning-color)',
            'ticket_created': 'var(--warning-color)',
            'ticket_updated': 'var(--warning-color)',
            'ticket_closed': 'var(--success-color)',
            'user_updated': 'var(--info-color)',
            'report_generated': 'var(--primary-color)',
            'export': 'var(--primary-color)',
        };
        return colorMap[type] || 'var(--text-secondary)';
    };

    const getActivityLabel = (type) => {
        const labelMap = {
            'login': 'fez login',
            'logout': 'saiu do sistema',
            'order_created': 'criou um pedido',
            'order_approved': 'aprovou um pedido',
            'order_rejected': 'rejeitou um pedido',
            'order_updated': 'atualizou um pedido',
            'order_deleted': 'excluiu um pedido',
            'product_created': 'criou um produto',
            'product_updated': 'atualizou um produto',
            'product_deleted': 'excluiu um produto',
            'project_created': 'criou um projeto',
            'project_updated': 'atualizou um projeto',
            'ticket_created': 'abriu um ticket',
            'ticket_updated': 'atualizou um ticket',
            'ticket_closed': 'fechou um ticket',
            'user_updated': 'atualizou perfil',
            'report_generated': 'gerou um relatório',
            'export': 'exportou dados',
        };
        return labelMap[type] || 'realizou uma ação';
    };

    const formatTimeAgo = (timestamp) => {
        if (!timestamp) return 'agora';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'agora mesmo';
        if (diffMins < 60) return `${diffMins}min atrás`;
        if (diffHours < 24) return `${diffHours}h atrás`;
        if (diffDays < 7) return `${diffDays}d atrás`;
        return date.toLocaleDateString('pt-BR');
    };

    return (
        <div className={styles.activityItem}>
            <div
                className={styles.activityIcon}
                style={{ backgroundColor: `${getActivityColor(activity.type)}20`, color: getActivityColor(activity.type) }}
            >
                {getActivityIcon(activity.type)}
            </div>
            <div className={styles.activityContent}>
                <div className={styles.activityHeader}>
                    <span className={styles.activityUser}>
                        <strong>{activity.userName || activity.userEmail || 'Usuário'}</strong>
                    </span>
                    <span className={styles.activityAction}>
                        {getActivityLabel(activity.type)}
                    </span>
                    {activity.target && (
                        <span className={styles.activityTarget}>
                            <FiArrowRight size={12} />
                            <span>{activity.target}</span>
                        </span>
                    )}
                </div>
                {activity.details && (
                    <div className={styles.activityDetails}>
                        {activity.details}
                    </div>
                )}
                <div className={styles.activityTime}>
                    <FiClock size={12} />
                    <span>{formatTimeAgo(activity.timestamp)}</span>
                </div>
            </div>
        </div>
    );
};

// ============================================
// MAIN COMPONENT
// ============================================
const DiretoriaDashboard = ({ user }) => {
    const { t } = useTranslation();
    const [lastRefreshed, setLastRefreshed] = useState(new Date());
    const [notifications, setNotifications] = useState([]);
    const [toasts, setToasts] = useState([]);

    // Approval state
    const [approvalModal, setApprovalModal] = useState(null);
    const [expandedOrders, setExpandedOrders] = useState({});
    const [actionLoading, setActionLoading] = useState({});
    const [showAllSales, setShowAllSales] = useState(false);
    const [showAllPurchases, setShowAllPurchases] = useState(false);
    const [showAllActivities, setShowAllActivities] = useState(false);

    // Fetch necessary data
    const { data: salesOrders, loading: loadingSales, updateDocument: updateSalesOrder, refreshData: refreshSales } = useFirestore('sales-orders', 'createdAt', 'desc');
    const { data: developmentProjects, loading: loadingProjects } = useFirestore('development-projects', 'code');
    const { data: purchaseOrders, loading: loadingPurchase, updateDocument: updatePurchaseOrder, refreshData: refreshPurchases } = useFirestore('purchase-orders', 'createdAt', 'desc');
    const { data: tickets, loading: loadingTickets } = useFirestore('help-desk-tickets', 'createdAt', 'desc');
    const { data: products, loading: loadingProducts } = useFirestore('products');
    const { data: rawMaterials, loading: loadingMaterials } = useFirestore('raw-materials');
    const { data: activities, loading: loadingActivities, refreshData: refreshActivities } = useFirestore('user-activities', 'timestamp', 'desc');

    // Presence hook for online users
    const { onlineUsers, loading: presenceLoading } = usePresence();

    // --- Toast System ---
    const addToast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 5000);
    }, []);

    // --- 1. KPI Calculations ---
    const kpiData = useMemo(() => {
        const deliveredOrders = (salesOrders || []).filter(order => order.status === 'delivered' || order.status === 'shipped');
        const pendingOrdersArray = (salesOrders || []).filter(order => order.status === 'pending');
        const approvedOrdersArray = (salesOrders || []).filter(order => order.status === 'approved');
        const totalOrders = pendingOrdersArray.length + approvedOrdersArray.length;

        const totalRevenue = deliveredOrders.reduce((sum, order) => sum + (order.calculatedValues?.total || 0), 0);
        const avgTicket = deliveredOrders.length > 0 ? totalRevenue / deliveredOrders.length : 0;

        const activeProjects = (developmentProjects || []).filter(proj => proj.situation === 'Active').length;
        const onHoldProjects = (developmentProjects || []).filter(proj => proj.situation === 'On Hold').length;

        const pendingPurchaseOrders = (purchaseOrders || []).filter(order => order.status === 'pending').length;
        const openTickets = (tickets || []).filter(ticket => ticket.status === 'Aberto' || ticket.status === 'Em Progresso').length;
        const criticalTickets = (tickets || []).filter(ticket =>
            (ticket.prioridade?.toLowerCase() === 'critico' || ticket.prioridadeRaw?.toLowerCase() === 'critico') &&
            (ticket.status === 'Aberto' || ticket.status === 'Em Progresso')
        ).length;

        const totalProducts = (products || []).length;
        const totalRawMaterials = (rawMaterials || []).length;

        return {
            totalRevenue,
            totalOrders,
            avgTicket,
            activeProjects,
            onHoldProjects,
            pendingPurchaseOrders,
            openTickets,
            criticalTickets,
            totalProducts,
            totalRawMaterials,
            deliveredOrders,
            pendingOrders: pendingOrdersArray,
            approvedOrders: approvedOrdersArray,
            pendingPurchaseOrdersList: (purchaseOrders || []).filter(order => order.status === 'pending'),
        };
    }, [salesOrders, developmentProjects, purchaseOrders, tickets, products, rawMaterials]);

    // --- 2. Pending Orders for Approval ---
    const pendingSalesOrders = useMemo(() => {
        return (salesOrders || [])
            .filter(order => order.status === 'pending')
            .sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(0);
                const dateB = b.createdAt?.toDate?.() || new Date(0);
                return dateB - dateA;
            });
    }, [salesOrders]);

    const pendingPurchaseOrdersList = useMemo(() => {
        return (purchaseOrders || []).filter(order => order.status === 'pending');
    }, [purchaseOrders]);

    // --- 3. Recent Activities ---
    const recentActivities = useMemo(() => {
        if (!activities) return [];
        // Filter out system activities if needed
        return activities
            .filter(activity => activity.type !== 'system')
            .slice(0, showAllActivities ? 20 : 10);
    }, [activities, showAllActivities]);

    // --- 4. Approval Handlers ---
    const handleApproveOrder = useCallback(async (orderId, type, approvalData) => {
        setActionLoading(prev => ({ ...prev, [orderId]: true }));

        try {
            const updateData = {
                status: approvalData.status,
                approvalNotes: approvalData.notes,
                approvedBy: user?.nome || user?.email?.split('@')[0] || 'Diretoria',
                approvedAt: new Date(),
                updatedAt: new Date()
            };

            let result;
            if (type === 'sales') {
                result = await updateSalesOrder(orderId, updateData);
                if (result.success) {
                    addToast(`Pedido de venda ${approvalData.orderCode} ${approvalData.status === 'approved' ? 'aprovado' : 'rejeitado'} com sucesso!`, 'success');
                    await refreshSales();
                }
            } else {
                result = await updatePurchaseOrder(orderId, updateData);
                if (result.success) {
                    addToast(`Pedido de compra ${approvalData.orderCode} ${approvalData.status === 'approved' ? 'aprovado' : 'rejeitado'} com sucesso!`, 'success');
                    await refreshPurchases();
                }
            }

            if (!result?.success) {
                addToast(`Erro ao processar: ${result?.error || 'Erro desconhecido'}`, 'error');
            }
        } catch (error) {
            console.error('Error processing approval:', error);
            addToast('Erro ao processar aprovação', 'error');
        } finally {
            setActionLoading(prev => ({ ...prev, [orderId]: false }));
            setApprovalModal(null);
        }
    }, [user, updateSalesOrder, updatePurchaseOrder, refreshSales, refreshPurchases, addToast]);

    const openApprovalModal = (order, type) => {
        setApprovalModal({ order, type });
    };

    const closeApprovalModal = () => {
        setApprovalModal(null);
    };

    const toggleExpanded = (orderId) => {
        setExpandedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    // --- 5. Formatting Helpers ---
    const formatCurrency = (value) => {
        if (value === undefined || value === null || isNaN(value)) return 'R$ 0,00';
        return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatShortCurrency = (value) => {
        if (value === undefined || value === null || isNaN(value)) return 'R$ 0';
        if (value >= 1_000_000) {
            return `R$ ${(value / 1_000_000).toFixed(1)}M`;
        }
        if (value >= 1_000) {
            return `R$ ${(value / 1_000).toFixed(1)}k`;
        }
        return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return '—';
        try {
            if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('pt-BR');
            if (typeof dateValue === 'string') return new Date(dateValue).toLocaleDateString('pt-BR');
            if (dateValue instanceof Date) return dateValue.toLocaleDateString('pt-BR');
            return '—';
        } catch {
            return '—';
        }
    };

    const renderNotificationIcon = (iconName, type) => {
        if (iconName === 'clock') return <FiClock size={16} />;
        if (iconName === 'alert') return <FiAlertCircle size={16} />;
        if (type === 'critical') return <FiX size={14} />;
        return <FiInfo size={14} />;
    };

    // --- 6. Toast Renderer ---
    const renderToasts = () => {
        if (toasts.length === 0) return null;
        return (
            <div className={styles.toastContainer}>
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                    />
                ))}
            </div>
        );
    };

    // --- 7. Render Pending Orders Section ---
    const renderPendingOrders = (orders, type, title, emptyMessage, maxDisplay = 3) => {
        const isSales = type === 'sales';
        const showAll = isSales ? showAllSales : showAllPurchases;
        const setShowAll = isSales ? setShowAllSales : setShowAllPurchases;
        const displayOrders = showAll ? orders : orders.slice(0, maxDisplay);
        const hasMore = orders.length > maxDisplay;

        if (orders.length === 0) {
            return (
                <div className={styles.emptyPendingState}>
                    <FiCheckCircle size={48} />
                    <div className={styles.emptyTitle}>{emptyMessage}</div>
                </div>
            );
        }

        return (
            <div>
                {displayOrders.map((order) => {
                    const isExpanded = expandedOrders[order.id];
                    const isLoading = actionLoading[order.id];
                    const totalValue = order.calculatedValues?.total || order.total || 0;
                    const itemCount = order.items?.length || 0;
                    const clientName = isSales ? order.clientName : order.supplierName;

                    return (
                        <div key={order.id} className={styles.orderCard}>
                            {/* Order Header */}
                            <div className={styles.orderCardHeader} onClick={() => toggleExpanded(order.id)}>
                                <div className={styles.orderInfo}>
                                    <span className={styles.orderCode}>#{order.code}</span>
                                    <span className={styles.orderDate}>{formatDate(order.createdAt || order.orderDate)}</span>
                                    <span className={styles.orderClient}>{clientName}</span>
                                    <span className={styles.orderBadge}>{itemCount} itens</span>
                                    <span className={styles.orderTotal}>{formatCurrency(totalValue)}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openApprovalModal(order, type);
                                        }}
                                        disabled={isLoading}
                                        className={styles.approveBtn}
                                    >
                                        {isLoading ? (
                                            <FiRefreshCw size={14} className={styles.btnSpinner} />
                                        ) : (
                                            <FiCheck size={14} />
                                        )}
                                        Aprovar
                                    </button>
                                    {isExpanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                                </div>
                            </div>

                            {/* Order Details (Expanded) */}
                            {isExpanded && (
                                <div className={styles.orderCardBody}>
                                    <div className={styles.orderDetailsGrid}>
                                        <div>
                                            <span className={styles.orderDetailsLabel}>Responsável</span>
                                            <div className={styles.orderDetailsValue}>{order.responsible || order.createdBy || '—'}</div>
                                        </div>
                                        <div>
                                            <span className={styles.orderDetailsLabel}>Forma de Pagamento</span>
                                            <div className={styles.orderDetailsValue}>{order.paymentMethodName || order.paymentMethod || '—'}</div>
                                        </div>
                                        {isSales && order.deliveryAddress && (
                                            <div className={styles.fullWidth}>
                                                <span className={styles.orderDetailsLabel}>Endereço de Entrega</span>
                                                <div className={styles.orderDetailsValue}>{order.deliveryAddress}</div>
                                            </div>
                                        )}
                                        {!isSales && order.supplierAddress && (
                                            <div className={styles.fullWidth}>
                                                <span className={styles.orderDetailsLabel}>Endereço do Fornecedor</span>
                                                <div className={styles.orderDetailsValue}>{order.supplierAddress}</div>
                                            </div>
                                        )}
                                        {order.commercialNotes && (
                                            <div className={styles.fullWidth}>
                                                <span className={styles.orderDetailsLabel}>Observações</span>
                                                <div className={styles.orderDetailsValue} style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                                                    {order.commercialNotes}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Items Table */}
                                    {order.items && order.items.length > 0 && (
                                        <div>
                                            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                                                Itens do Pedido
                                            </div>
                                            <table className={styles.expandedItemsTable}>
                                                <thead>
                                                    <tr>
                                                        <th>Produto</th>
                                                        <th className={styles.textRight}>Qtd</th>
                                                        <th className={styles.textRight}>Preço Unit.</th>
                                                        <th className={styles.textRight}>Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {order.items.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.name || item.productName || item.inciName || 'Item'}</td>
                                                            <td className={styles.textRight}>{item.quantity || 0}</td>
                                                            <td className={styles.textRight}>{formatCurrency(item.unitPrice || 0)}</td>
                                                            <td className={styles.textRight}>{formatCurrency((item.quantity || 0) * (item.unitPrice || 0))}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr className={styles.totalRow}>
                                                        <td colSpan="3" className={styles.textRight}>Total</td>
                                                        <td className={`${styles.textRight} ${styles.totalAmount}`}>
                                                            {formatCurrency(totalValue)}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {hasMore && (
                    <button
                        onClick={() => setShowAll(!showAll)}
                        className={styles.showMoreBtn}
                    >
                        {showAll ? 'Mostrar menos' : `Ver todos (${orders.length - maxDisplay} mais)`}
                    </button>
                )}
            </div>
        );
    };

    // --- RENDER ---
    return (
        <div className={styles.dashboardContainer}>
            {renderToasts()}

            {/* Approval Modal */}
            {approvalModal && (
                <ApprovalModal
                    order={approvalModal.order}
                    type={approvalModal.type}
                    onClose={closeApprovalModal}
                    onConfirm={handleApproveOrder}
                    t={t}
                />
            )}

            {/* Header */}
            <div className={styles.dashboardHeader}>
                <div className={styles.headerMeta}>
                    <span className={styles.lastUpdate}>
                        <FiClock size={14} /> Last updated: {lastRefreshed.toLocaleTimeString('pt-BR')}
                    </span>
                    <span className={styles.userInfo}>
                        <FiUser size={14} /> {user?.role || 'Diretoria'}
                    </span>
                    <button
                        onClick={() => {
                            refreshSales();
                            refreshPurchases();
                            refreshActivities();
                            setLastRefreshed(new Date());
                            addToast('Dados atualizados!', 'success');
                        }}
                        style={{
                            background: 'var(--bg-tertiary)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.85rem',
                            color: 'var(--text-secondary)'
                        }}
                    >
                        <FiRefreshCw size={14} /> Atualizar
                    </button>
                </div>
            </div>

            {/* Executive Summary Cards */}
            <div className={styles.metricsGrid}>
                <div className={styles.metricsCard}>
                    <div className={styles.metricsHeader}>
                        <div className={styles.metricsIcon} style={{ backgroundColor: 'var(--success-light)', color: 'var(--success-color)' }}>
                            <FiDollarSign size={22} />
                        </div>
                    </div>
                    <div className={styles.metricsValue}>{formatShortCurrency(kpiData.totalRevenue)}</div>
                    <div className={styles.metricsLabel}>Total Revenue</div>
                    <div className={styles.metricsSubtitle}>
                        <FiTrendingUp size={14} /> From delivered orders
                    </div>
                </div>

                <div className={styles.metricsCard}>
                    <div className={styles.metricsHeader}>
                        <div className={styles.metricsIcon} style={{ backgroundColor: 'var(--info-light)', color: 'var(--info-color)' }}>
                            <FiPackage size={22} />
                        </div>
                    </div>
                    <div className={styles.metricsValue}>{kpiData.totalOrders}</div>
                    <div className={styles.metricsLabel}>Total Orders</div>
                    <div className={styles.metricsSubtitle}>
                        <FiList size={14} /> {kpiData.pendingOrders.length} pending / {kpiData.approvedOrders.length} approved
                    </div>
                </div>

                <div className={styles.metricsCard}>
                    <div className={styles.metricsHeader}>
                        <div className={styles.metricsIcon} style={{ backgroundColor: 'var(--warning-light)', color: 'var(--warning-color)' }}>
                            <FiFileText size={22} />
                        </div>
                    </div>
                    <div className={styles.metricsValue}>{kpiData.activeProjects}</div>
                    <div className={styles.metricsLabel}>Active Projects</div>
                    <div className={styles.metricsSubtitle}>
                        <FiClock size={14} /> {kpiData.onHoldProjects} on hold
                    </div>
                </div>

                <div className={styles.metricsCard}>
                    <div className={styles.metricsHeader}>
                        <div className={styles.metricsIcon} style={{ backgroundColor: 'var(--danger-light)', color: 'var(--danger-color)' }}>
                            <FiAlertCircle size={22} />
                        </div>
                        <span className={styles.metricsPercentage}>{kpiData.openTickets > 0 ? '⚠️' : '✅'}</span>
                    </div>
                    <div className={styles.metricsValue}>{kpiData.openTickets}</div>
                    <div className={styles.metricsLabel}>Open Tickets</div>
                    <div className={styles.metricsSubtitle}>
                        <FiCheckCircle size={14} /> {kpiData.criticalTickets} critical
                    </div>
                </div>

                {/* New Online Users Metric Card */}
                <div className={styles.metricsCard}>
                    <div className={styles.metricsHeader}>
                        <div className={styles.metricsIcon} style={{ backgroundColor: 'var(--info-light)', color: 'var(--info-color)' }}>
                            <FiUsers size={22} />
                        </div>
                        <span className={styles.metricsPercentage} style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                            {presenceLoading ? '...' : `${onlineUsers.length} online`}
                        </span>
                    </div>
                    <div className={styles.metricsValue}>{onlineUsers.length}</div>
                    <div className={styles.metricsLabel}>Usuários Online</div>
                    <div className={styles.metricsSubtitle}>
                        <FiClock size={14} /> {onlineUsers.length} usuários ativos agora
                    </div>
                </div>
            </div>

            {/* Notifications Bar */}
            {notifications.length > 0 && (
                <div className={`${styles.card} ${styles.insights}`} style={{ marginBottom: '24px', borderLeft: '4px solid var(--warning-color)' }}>
                    <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <FiAlertCircle size={20} color="var(--warning-color)" />
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
                            {notifications.map((notification, index) => {
                                const messageText = typeof notification.message === 'string'
                                    ? notification.message
                                    : String(notification.message || '');

                                return (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '4px 12px',
                                            borderRadius: '20px',
                                            backgroundColor: notification.type === 'critical' ? 'var(--danger-light)' : 'var(--info-light)',
                                            color: notification.type === 'critical' ? 'var(--danger-color)' : 'var(--info-color)',
                                            fontSize: '0.85rem',
                                            cursor: notification.action ? 'pointer' : 'default'
                                        }}
                                        onClick={notification.action}
                                    >
                                        {renderNotificationIcon(notification.icon, notification.type)}
                                        <span>{messageText}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>

                {/* Left Column: Sales Orders Pending Approval */}
                <div>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}>
                                <div className={styles.cardIcon}><FiShoppingCart size={20} /></div>
                                <h3>Pedidos de Venda Pendentes</h3>
                            </div>
                            <span className={styles.sectionBadge}>{pendingSalesOrders.length} aguardando</span>
                        </div>
                        <div className={styles.cardContent}>
                            {loadingSales ? (
                                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>
                                    <FiRefreshCw size={24} className={styles.spinner} style={{ animation: 'spin 0.8s linear infinite' }} />
                                    <p style={{ fontSize: '0.95rem' }}>Carregando pedidos...</p>
                                </div>
                            ) : (
                                renderPendingOrders(
                                    pendingSalesOrders,
                                    'sales',
                                    'Pedidos de Venda',
                                    'Nenhum pedido de venda pendente de aprovação 🎉'
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column: Purchase Orders Pending Approval */}
                <div>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}>
                                <div className={styles.cardIcon}><FiTruck size={20} /></div>
                                <h3>Pedidos de Compra Pendentes</h3>
                            </div>
                            <span className={styles.sectionBadge}>{pendingPurchaseOrdersList.length} aguardando</span>
                        </div>
                        <div className={styles.cardContent}>
                            {loadingPurchase ? (
                                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>
                                    <FiRefreshCw size={24} className={styles.spinner} style={{ animation: 'spin 0.8s linear infinite' }} />
                                    <p style={{ fontSize: '0.95rem' }}>Carregando pedidos...</p>
                                </div>
                            ) : (
                                renderPendingOrders(
                                    pendingPurchaseOrdersList,
                                    'purchase',
                                    'Pedidos de Compra',
                                    'Nenhum pedido de compra pendente de aprovação 🎉'
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* Additional Metrics Section - Full Width */}
                <div style={{ gridColumn: 'span 2' }}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}>
                                <div className={styles.cardIcon}><FiBox size={20} /></div>
                                <h3>Resumo de Produtos e Materiais</h3>
                            </div>
                        </div>
                        <div className={styles.cardContent}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                                <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                                        {kpiData.totalProducts}
                                    </div>
                                    <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>Produtos Cadastrados</div>
                                </div>
                                <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                                        {kpiData.totalRawMaterials}
                                    </div>
                                    <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>Matérias-Primas</div>
                                </div>
                                <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--success-color)' }}>
                                        {kpiData.approvedOrders.length}
                                    </div>
                                    <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>Pedidos Aprovados</div>
                                </div>
                                <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--warning-color)' }}>
                                        {kpiData.pendingOrders.length + kpiData.pendingPurchaseOrders}
                                    </div>
                                    <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>Total Pendentes</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Activity Section - Full Width */}
                <div style={{ gridColumn: 'span 2', marginTop: '0' }}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}>
                                <div className={styles.cardIcon}><FiActivity size={20} /></div>
                                <h3>Atividades Recentes</h3>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span className={styles.sectionBadge}>
                                    {recentActivities.length} atividades
                                </span>
                                <button
                                    onClick={() => {
                                        refreshActivities();
                                        addToast('Atividades atualizadas!', 'success');
                                    }}
                                    className={styles.refreshSmallBtn}
                                    disabled={loadingActivities}
                                >
                                    <FiRefreshCw size={14} className={loadingActivities ? styles.spin : ''} />
                                </button>
                            </div>
                        </div>
                        <div className={styles.cardContent}>
                            {loadingActivities ? (
                                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>
                                    <FiRefreshCw size={24} className={styles.spinner} style={{ animation: 'spin 0.8s linear infinite' }} />
                                    <p style={{ fontSize: '0.95rem' }}>Carregando atividades...</p>
                                </div>
                            ) : recentActivities.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <FiActivity size={48} style={{ color: 'var(--text-muted)' }} />
                                    <p>Nenhuma atividade recente registrada</p>
                                    <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                                        As atividades dos usuários aparecerão aqui
                                    </span>
                                </div>
                            ) : (
                                <div className={styles.activityFeed}>
                                    {recentActivities.map((activity) => (
                                        <ActivityItem key={activity.id} activity={activity} />
                                    ))}
                                    {recentActivities.length >= 10 && (
                                        <button
                                            onClick={() => setShowAllActivities(!showAllActivities)}
                                            className={styles.showMoreBtn}
                                            style={{ marginTop: '16px' }}
                                        >
                                            {showAllActivities ? 'Mostrar menos' : 'Ver mais atividades'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Online Users Section - Full Width */}
                <div style={{ gridColumn: 'span 2', marginTop: '0' }}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}>
                                <div className={styles.cardIcon}><FiUsers size={20} /></div>
                                <h3>Usuários Online</h3>
                            </div>
                            <span className={styles.sectionBadge}>{onlineUsers.length} ativos</span>
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.onlineUsersGrid}>
                                {onlineUsers.length > 0 ? (
                                    onlineUsers.map(user => (
                                        <div key={user.id} className={styles.onlineUserCard}>
                                            <div className={styles.avatar}>
                                                {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>
                                            <div className={styles.userInfo}>
                                                <div className={styles.userName}>{user.displayName || user.email || 'Usuário'}</div>
                                                <div className={styles.userEmail}>{user.email}</div>
                                            </div>
                                            <div className={styles.userStatus}>
                                                <span className={styles.onlineDot} />
                                                Online
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className={styles.emptyState}>
                                        <FiUsers size={48} style={{ color: 'var(--text-muted)' }} />
                                        <p>Nenhum usuário online no momento</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiretoriaDashboard;